import { HighlightOffRounded as RoundedIcon } from '@material-ui/icons';
import { FC, useMemo, useState } from 'react';

import { usePropertySpaceList } from '@apis/propertiesApi';
import { PropertySpace } from '@apis/propertiesApi.types';
import { WithStyles, withStyles } from '@core/theme/utils/with-styles';
import { Flex } from '@shared/components/flex';
import { LocalLookup } from '@shared/components/local-lookup';

import { styles } from './LocalSpaceLookup.styles';

export interface LocalSpaceLookupProps extends WithStyles<typeof styles> {
  disabled?: boolean;
  error?: boolean;
  floor: string;
  label?: string;
  placeholder: string;
  propertyId?: Id;
  unit?: Id;
  unit_string: string;
  setSpace: (name: string, space?: PropertySpace) => void;
}

const LocalSpaceLookupComponent: FC<LocalSpaceLookupProps> = ({
  classes,
  disabled = false,
  error,
  floor,
  label,
  placeholder,
  propertyId,
  unit,
  unit_string,
  setSpace,
}) => {
  const [searchField, setSearchField] = useState<string>(unit_string || '');
  const [selectedSpace, setSelectedSpace] = useState<string>(unit ? unit.toString() : '');

  const propertySpaceList = usePropertySpaceList(propertyId as number, { enabled: !!propertyId });

  const propertySpaceData = useMemo(() => propertySpaceList?.data?.data || [], [propertySpaceList]);

  const filteredPropertySpaceData = useMemo(() => {
    if (searchField) {
      return propertySpaceData.filter((el) => {
        return (
          el.floor_identifier == floor &&
          el.floor_description &&
          el.name_string.toLowerCase().includes(searchField.toLowerCase())
        );
      });
    }

    return propertySpaceData.filter((el) => el.floor_identifier == floor && el.floor_description);
  }, [floor, propertySpaceData, searchField]);

  const handleSearchFieldChange = (search: string) => {
    setSearchField(search);

    setSpace(search, undefined);
  };

  const handleSelect = (space?: PropertySpace) => {
    setSpace('', space);
    setSelectedSpace(space?.floor_description || '');
    setSearchField('');
  };

  const renderSpaceOption = (space: PropertySpace) => {
    return (
      <Flex alignItems="center" classes={{ root: classes.option }}>
        {space.floor_description}
      </Flex>
    );
  };

  const fieldContent = useMemo(() => {
    if (selectedSpace) {
      return (
        <>
          {label && <span className={classes.label}>{label}</span>}
          <Flex
            alignItems="center"
            autoWidth={false}
            justifyContent="space-between"
            className={classes.spaceSelected}
            wrap="nowrap"
          >
            <span className={classes.spaceSelectedName}>{selectedSpace}</span>
            <RoundedIcon onClick={() => handleSelect(undefined)} classes={{ root: classes.clearIcon }} />
          </Flex>
        </>
      );
    }

    return (
      <LocalLookup
        classes={{ label: classes.label }}
        inputProps={{
          disabled: disabled || propertySpaceList.isFetching,
          error: error,
          placeholder: placeholder,
        }}
        label={label}
        optionsList={filteredPropertySpaceData}
        searchValue={searchField}
        withClearIcon={true}
        onSearchValueChange={handleSearchFieldChange}
        onSelect={handleSelect}
        renderOption={renderSpaceOption}
      />
    );
  }, [
    disabled,
    error,
    filteredPropertySpaceData,
    label,
    placeholder,
    propertySpaceList.isFetching,
    searchField,
    selectedSpace,
  ]);

  return (
    <Flex autoWidth={false} classes={{ root: classes.root }}>
      {fieldContent}
    </Flex>
  );
};

export const LocalSpaceLookup = withStyles(styles)(LocalSpaceLookupComponent);
